import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

const checkSuperAdmin = () => {
  try {
    if (localStorage.getItem('is_superadmin') === 'true') return true;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return !!user.is_superadmin;
  } catch { return false; }
};

const ALL_COMMANDS = [
  // ── Navigate ─────────────────────────────────────────────────────────────
  { id: 'nav-dash',      group: 'Navigate', icon: '🏠', label: 'Dashboard',              to: '/dashboard',            keywords: ['home', 'overview'] },
  { id: 'nav-analytics', group: 'Navigate', icon: '📊', label: 'Analytics Overview',      to: '/reporting/overview',   keywords: ['charts', 'stats', 'metrics'] },
  { id: 'nav-sla',       group: 'Navigate', icon: '🚨', label: 'SLA & Escalation Alerts', to: '/sla-alerts',           keywords: ['sla', 'escalation', 'breach'] },
  { id: 'nav-notif',     group: 'Navigate', icon: '🔔', label: 'Notifications',           to: '/notifications',        keywords: ['alerts', 'messages'] },
  { id: 'nav-profile',   group: 'Navigate', icon: '👤', label: 'My Profile',              to: '/profile',              keywords: ['account', 'settings', 'me'] },
  { id: 'nav-search',    group: 'Navigate', icon: '🔍', label: 'Search',                  to: '/search',               keywords: ['find', 'lookup', 'query'] },
  { id: 'nav-campaigns', group: 'Navigate', icon: '📣', label: 'Marketing Campaigns',     to: '/marketing/campaign/list', keywords: ['marketing', 'campaign', 'email blast'] },

  // ── Actions ───────────────────────────────────────────────────────────────
  { id: 'act-theme',   group: 'Actions', icon: '🌓', label: 'Toggle Light / Dark Theme', action: () => { document.body.classList.toggle('light-theme'); try { localStorage.setItem('theme', document.body.classList.contains('light-theme') ? 'light' : 'dark'); } catch {} }, keywords: ['theme', 'dark', 'light', 'mode', 'color'] },
  { id: 'act-signout', group: 'Actions', icon: '🚪', label: 'Sign Out',                  action: ({ navigate }) => { try { localStorage.clear(); } catch {} navigate('/login'); }, keywords: ['logout', 'exit', 'leave', 'signout'] },

  // ── Create ────────────────────────────────────────────────────────────────
  { id: 'c-lead',     group: 'Create', icon: '⚡', label: 'New Lead',        to: '/lead/input',           keywords: ['prospect', 'capture'] },
  { id: 'c-contact',  group: 'Create', icon: '👤', label: 'New Contact',     to: '/contact/input',        keywords: ['person', 'people'] },
  { id: 'c-customer', group: 'Create', icon: '🏢', label: 'New Customer',    to: '/customer/stage/input', keywords: ['account', 'client', 'onboard'] },
  { id: 'c-opp',      group: 'Create', icon: '🎯', label: 'New Opportunity', to: '/opportunity/input',    keywords: ['deal', 'pipeline'] },
  { id: 'c-quote',    group: 'Create', icon: '📄', label: 'New Quote',       to: '/quote/input',          keywords: ['proposal', 'estimate'] },
  { id: 'c-task',     group: 'Create', icon: '✅', label: 'New Task',        to: '/task/input',           keywords: ['todo', 'reminder', 'follow'] },
  { id: 'c-workflow', group: 'Create', icon: '🔄', label: 'New Workflow',    to: '/workflow/input',       keywords: ['automation', 'process'] },

  // ── Leads ────────────────────────────────────────────────────────────────
  { id: 'lm-v-leads',         group: 'Leads', icon: '📋', label: 'All Leads',                to: '/lead/list',                   keywords: ['prospects', 'pipeline'] },
  { id: 'lm-v-contacts',      group: 'Leads', icon: '👥', label: 'All Contacts',             to: '/contact/list',                keywords: ['people', 'persons'] },
  { id: 'lm-v-tasks',         group: 'Leads', icon: '✔',  label: 'All Tasks',                to: '/tasks/list',                  keywords: ['todos', 'followups'] },
  { id: 'lm-v-workflows',     group: 'Leads', icon: '🔄', label: 'All Workflows',            to: '/workflow/list',               keywords: ['automation'] },
  { id: 'lm-v-email-tpl',     group: 'Leads', icon: '📧', label: 'Email Templates',          to: '/lead/email-template/list',    keywords: ['email', 'template', 'marketing'] },
  { id: 'lm-v-sales-teams',   group: 'Leads', icon: '🫂', label: 'Sales Teams',              to: '/lead/sales-team/list',        keywords: ['team', 'staff'] },
  { id: 'lm-v-sales-regions', group: 'Leads', icon: '🗺',  label: 'Sales Regions',            to: '/lead/sales-region/list',      keywords: ['region', 'territory', 'area'] },
  { id: 'lm-v-sales-targets', group: 'Leads', icon: '🎯', label: 'Sales Targets',            to: '/lead/sales-target/list',      keywords: ['quota', 'goal', 'target'] },
  { id: 'lm-v-sales-perf',    group: 'Leads', icon: '📈', label: 'Sales Performance',        to: '/lead/sales-performance/list', keywords: ['kpi', 'metrics', 'performance'] },
  { id: 'lm-c-email-tpl',     group: 'Leads', icon: '📧', label: 'New Email Template',       to: '/lead/email-template/input',   keywords: ['email', 'template'] },
  { id: 'lm-c-sales-team',    group: 'Leads', icon: '🫂', label: 'New Sales Team',           to: '/lead/sales-team/input',       keywords: ['team'] },
  { id: 'lm-c-sales-region',  group: 'Leads', icon: '🗺',  label: 'New Sales Region',         to: '/lead/sales-region/input',     keywords: ['region', 'territory'] },
  { id: 'lm-c-sales-target',  group: 'Leads', icon: '🎯', label: 'New Sales Target',         to: '/lead/sales-target/input',     keywords: ['quota', 'goal'] },
  { id: 'lm-c-sales-perf',    group: 'Leads', icon: '📈', label: 'New Sales Performance',    to: '/lead/sales-performance/input',keywords: ['kpi'] },

  // ── Opportunities ─────────────────────────────────────────────────────────
  { id: 'om-v-opps',       group: 'Opportunities', icon: '💼', label: 'All Opportunities',          to: '/opportunity/list',             keywords: ['deals', 'pipeline'] },
  { id: 'om-v-stages',     group: 'Opportunities', icon: '🏷',  label: 'Opportunity Stages',         to: '/opportunity/stage/list',       keywords: ['stage', 'pipeline'] },
  { id: 'om-v-activities', group: 'Opportunities', icon: '📅', label: 'Opportunity Activities',     to: '/opportunity/activity/list',    keywords: ['activities'] },
  { id: 'om-v-docs',       group: 'Opportunities', icon: '📎', label: 'Opportunity Documents',      to: '/opportunity/document/list',    keywords: ['files', 'attachments'] },
  { id: 'om-v-followups',  group: 'Opportunities', icon: '🔁', label: 'Opportunity Follow-ups',     to: '/opportunity/follow-up/list',   keywords: ['followup', 'reminder'] },
  { id: 'om-v-notes',      group: 'Opportunities', icon: '📝', label: 'Opportunity Notes',          to: '/opportunity/note/list',        keywords: ['notes'] },
  { id: 'om-v-comments',   group: 'Opportunities', icon: '💬', label: 'Opportunity Comments',       to: '/opportunity/comment/list',     keywords: ['comments'] },
  { id: 'om-v-tasks',      group: 'Opportunities', icon: '✅', label: 'Opportunity Tasks',          to: '/opportunity/task/list',        keywords: ['tasks'] },
  { id: 'om-v-events',     group: 'Opportunities', icon: '📆', label: 'Opportunity Events',         to: '/opportunity/event/list',       keywords: ['events'] },
  { id: 'om-v-meetings',   group: 'Opportunities', icon: '🤝', label: 'Opportunity Meetings',       to: '/opportunity/meeting/list',     keywords: ['meetings'] },
  { id: 'om-v-calls',      group: 'Opportunities', icon: '📞', label: 'Opportunity Calls',          to: '/opportunity/call/list',        keywords: ['calls', 'phone'] },
  { id: 'om-v-sources',    group: 'Opportunities', icon: '🔗', label: 'Opportunity Sources',        to: '/opportunity/source/list',      keywords: ['source', 'channel'] },
  { id: 'om-v-priorities', group: 'Opportunities', icon: '🔴', label: 'Opportunity Priorities',     to: '/opportunity/priority/list',    keywords: ['priority', 'urgency'] },
  { id: 'om-v-statuses',   group: 'Opportunities', icon: '🚦', label: 'Opportunity Statuses',       to: '/opportunity/status/list',      keywords: ['status'] },
  { id: 'om-v-types',      group: 'Opportunities', icon: '📂', label: 'Opportunity Types',          to: '/opportunity/type/list',        keywords: ['type'] },
  { id: 'om-v-categories', group: 'Opportunities', icon: '🗂',  label: 'Opportunity Categories',     to: '/opportunity/category/list',    keywords: ['category'] },
  { id: 'om-v-subcats',    group: 'Opportunities', icon: '🗂',  label: 'Opportunity Sub-Categories', to: '/opportunity/subcategory/list', keywords: ['subcategory'] },
  { id: 'om-v-tags',       group: 'Opportunities', icon: '🏷',  label: 'Opportunity Tags',           to: '/opportunity/tag/list',         keywords: ['tags', 'labels'] },
  { id: 'om-v-ratings',    group: 'Opportunities', icon: '⭐', label: 'Opportunity Ratings',        to: '/opportunity/rating/list',      keywords: ['rating', 'score'] },
  { id: 'om-c-stage',      group: 'Opportunities', icon: '🏷',  label: 'New Opportunity Stage',        to: '/opportunity/stage/input',        keywords: ['stage'] },
  { id: 'om-c-source',     group: 'Opportunities', icon: '🔗', label: 'New Opportunity Source',       to: '/opportunity/source/input',       keywords: ['source'] },
  { id: 'om-c-priority',   group: 'Opportunities', icon: '🔴', label: 'New Priority Level',           to: '/opportunity/priority/input',     keywords: ['priority'] },
  { id: 'om-c-status',     group: 'Opportunities', icon: '🚦', label: 'New Opportunity Status',       to: '/opportunity/status/input',       keywords: ['status'] },
  { id: 'om-c-type',       group: 'Opportunities', icon: '📂', label: 'New Opportunity Type',         to: '/opportunity/type/input',         keywords: ['type'] },
  { id: 'om-c-category',   group: 'Opportunities', icon: '🗂',  label: 'New Opportunity Category',     to: '/opportunity/category/input',     keywords: ['category'] },
  { id: 'om-c-subcat',     group: 'Opportunities', icon: '🗂',  label: 'New Opportunity Sub-Category', to: '/opportunity/subcategory/input',  keywords: ['subcategory'] },
  { id: 'om-c-tag',        group: 'Opportunities', icon: '🏷',  label: 'New Opportunity Tag',          to: '/opportunity/tag/input',          keywords: ['tag', 'label'] },
  { id: 'om-c-rating',     group: 'Opportunities', icon: '⭐', label: 'New Opportunity Rating',       to: '/opportunity/rating/input',       keywords: ['rating', 'score'] },
  { id: 'om-c-note',       group: 'Opportunities', icon: '📝', label: 'New Opportunity Note',         to: '/opportunity/note/input',         keywords: ['note'] },
  { id: 'om-c-followup',   group: 'Opportunities', icon: '🔁', label: 'New Opportunity Follow-up',    to: '/opportunity/follow-up/input',    keywords: ['followup'] },
  { id: 'om-c-activity',   group: 'Opportunities', icon: '📅', label: 'New Opportunity Activity',     to: '/opportunity/activity/input',     keywords: ['activity'] },
  { id: 'om-c-doc',        group: 'Opportunities', icon: '📎', label: 'New Opportunity Document',     to: '/opportunity/document/input',     keywords: ['document', 'file'] },
  { id: 'om-c-task',       group: 'Opportunities', icon: '✅', label: 'New Opportunity Task',         to: '/opportunity/task/input',         keywords: ['task', 'todo'] },
  { id: 'om-c-event',      group: 'Opportunities', icon: '📆', label: 'New Opportunity Event',        to: '/opportunity/event/input',        keywords: ['event'] },
  { id: 'om-c-meeting',    group: 'Opportunities', icon: '🤝', label: 'New Opportunity Meeting',      to: '/opportunity/meeting/input',      keywords: ['meeting'] },
  { id: 'om-c-call',       group: 'Opportunities', icon: '📞', label: 'New Opportunity Call',         to: '/opportunity/call/input',         keywords: ['call', 'phone'] },

  // ── Customers ─────────────────────────────────────────────────────────────
  { id: 'cm-v-customers',    group: 'Customers', icon: '🏢', label: 'All Customers',            to: '/customer/list',                    keywords: ['accounts', 'clients'] },
  { id: 'cm-v-interactions', group: 'Customers', icon: '🤝', label: 'Customer Interactions',    to: '/customer/interactions/list',       keywords: ['interactions', 'touchpoints'] },
  { id: 'cm-v-notes',        group: 'Customers', icon: '📝', label: 'Customer Notes',           to: '/customer/note/list',               keywords: ['notes', 'comments'] },
  { id: 'cm-v-activities',   group: 'Customers', icon: '📅', label: 'Customer Activities',      to: '/customer/activity/list',           keywords: ['activities'] },
  { id: 'cm-v-act-types',    group: 'Customers', icon: '🏷',  label: 'Activity Types',           to: '/customer/activity-type/list',      keywords: ['types'] },
  { id: 'cm-v-comm-logs',    group: 'Customers', icon: '💬', label: 'Communication Logs',       to: '/customer/communication-log/list',  keywords: ['logs', 'history', 'comms'] },
  { id: 'cm-v-docs',         group: 'Customers', icon: '📎', label: 'Customer Documents',       to: '/customer/document/list',           keywords: ['files', 'attachments'] },
  { id: 'cm-v-reminders',    group: 'Customers', icon: '⏰', label: 'Customer Reminders',       to: '/customer/reminder/list',           keywords: ['reminders', 'alerts'] },
  { id: 'cm-v-sales-stages', group: 'Customers', icon: '🏷',  label: 'Customer Sales Stages',    to: '/customer/sales-stage/list',        keywords: ['stages', 'pipeline'] },
  { id: 'cm-v-sales-status', group: 'Customers', icon: '🚦', label: 'Customer Sales Status',    to: '/customer/sales-status/list',       keywords: ['status'] },
  { id: 'cm-v-forecasts',    group: 'Customers', icon: '📈', label: 'Customer Sales Forecasts', to: '/customer/sales-forecast/list',     keywords: ['forecast', 'revenue'] },
  { id: 'cm-v-webhooks',     group: 'Customers', icon: '🔗', label: 'Webhook Configs',          to: '/customer/webhook-config/list',     keywords: ['webhook', 'integration', 'api'] },
  { id: 'cm-v-async-logs',   group: 'Customers', icon: '📜', label: 'Async Event Logs',         to: '/customer/async-event-log/list',    keywords: ['async', 'events', 'logs'], superadminOnly: true },
  { id: 'cm-v-gw-logs',      group: 'Customers', icon: '📡', label: 'Gateway Message Logs',     to: '/customer/gateway-message-log/list',keywords: ['gateway', 'messages', 'logs'], superadminOnly: true },
  { id: 'cm-v-sync-logs',    group: 'Customers', icon: '🔄', label: 'Customer Sync Logs',       to: '/customer/sync-log/list',           keywords: ['sync', 'logs'], superadminOnly: true },
  { id: 'cm-v-change-logs',  group: 'Customers', icon: '📋', label: 'Customer Change Logs',     to: '/customer/change-log/list',         keywords: ['changes', 'audit'], superadminOnly: true },
  { id: 'cm-c-interaction',  group: 'Customers', icon: '🤝', label: 'New Customer Interaction', to: '/customer/interaction/input',       keywords: ['interaction'] },
  { id: 'cm-c-note',         group: 'Customers', icon: '📝', label: 'New Customer Note',        to: '/customer/note/input',              keywords: ['note'] },
  { id: 'cm-c-activity',     group: 'Customers', icon: '📅', label: 'New Customer Activity',    to: '/customer/activity/input',          keywords: ['activity'] },
  { id: 'cm-c-act-type',     group: 'Customers', icon: '🏷',  label: 'New Activity Type',        to: '/customer/activity-type/input',     keywords: ['type'] },
  { id: 'cm-c-comm-log',     group: 'Customers', icon: '💬', label: 'New Communication Log',    to: '/customer/communication-log/input', keywords: ['log', 'communication'] },
  { id: 'cm-c-doc',          group: 'Customers', icon: '📎', label: 'New Customer Document',    to: '/customer/document/input',          keywords: ['document', 'file'] },
  { id: 'cm-c-reminder',     group: 'Customers', icon: '⏰', label: 'New Customer Reminder',    to: '/customer/reminder/input',          keywords: ['reminder', 'alert'] },
  { id: 'cm-c-sales-stage',  group: 'Customers', icon: '🏷',  label: 'New Customer Sales Stage', to: '/customer/sales-stage/input',       keywords: ['stage'] },
  { id: 'cm-c-sales-status', group: 'Customers', icon: '🚦', label: 'New Customer Sales Status',to: '/customer/sales-status/input',      keywords: ['status'] },
  { id: 'cm-c-forecast',     group: 'Customers', icon: '📈', label: 'New Sales Forecast',       to: '/customer/sales-forecast/input',    keywords: ['forecast'] },
  { id: 'cm-c-webhook',      group: 'Customers', icon: '🔗', label: 'New Webhook Config',       to: '/customer/webhook-config/input',    keywords: ['webhook', 'api'] },

  // ── Quotes ────────────────────────────────────────────────────────────────
  { id: 'qm-v-quotes',     group: 'Quotes', icon: '📑', label: 'All Quotes',          to: '/quote/list',            keywords: ['proposals', 'estimates'] },
  { id: 'qm-v-products',   group: 'Quotes', icon: '📦', label: 'Products',             to: '/quote/product/list',    keywords: ['items', 'catalog', 'sku'] },
  { id: 'qm-v-statuses',   group: 'Quotes', icon: '🚦', label: 'Quote Statuses',       to: '/quote/status/list',     keywords: ['status'] },
  { id: 'qm-v-line-items', group: 'Quotes', icon: '📊', label: 'Line Items',           to: '/quote/line-item/list',  keywords: ['items', 'lines', 'breakdown'] },
  { id: 'qm-v-versions',   group: 'Quotes', icon: '📋', label: 'Quote Versions',       to: '/quote/version/list',    keywords: ['versions', 'revisions'] },
  { id: 'qm-v-docs',       group: 'Quotes', icon: '📎', label: 'Quote Documents',      to: '/quote/document/list',   keywords: ['files', 'attachments'] },
  { id: 'qm-v-tasks',      group: 'Quotes', icon: '✅', label: 'Quote Tasks',          to: '/quote/task/list',       keywords: ['tasks'] },
  { id: 'qm-v-followups',  group: 'Quotes', icon: '🔁', label: 'Quote Follow-Ups',     to: '/quote/follow-up/list',  keywords: ['followup'] },
  { id: 'qm-v-payments',   group: 'Quotes', icon: '💳', label: 'Quote Payments',       to: '/quote/payment/list',    keywords: ['payments', 'billing', 'invoice'] },
  { id: 'qm-v-approvals',  group: 'Quotes', icon: '✔',  label: 'Quote Approvals',      to: '/quote/approval/list',   keywords: ['approvals', 'sign-off'] },
  { id: 'qm-v-comments',   group: 'Quotes', icon: '💬', label: 'Quote Comments',       to: '/quote/comment/list',    keywords: ['comments'] },
  { id: 'qm-c-product',    group: 'Quotes', icon: '📦', label: 'New Product',          to: '/quote/product/input',   keywords: ['product', 'item', 'sku'] },
  { id: 'qm-c-status',     group: 'Quotes', icon: '🚦', label: 'New Quote Status',     to: '/quote/status/input',    keywords: ['status'] },
  { id: 'qm-c-line-item',  group: 'Quotes', icon: '📊', label: 'New Line Item',        to: '/quote/line-item/input', keywords: ['line item'] },
  { id: 'qm-c-version',    group: 'Quotes', icon: '📋', label: 'New Quote Version',    to: '/quote/version/input',   keywords: ['version', 'revision'] },
  { id: 'qm-c-payment',    group: 'Quotes', icon: '💳', label: 'New Payment',          to: '/quote/payment/input',   keywords: ['payment', 'billing'] },
  { id: 'qm-c-approval',   group: 'Quotes', icon: '✔',  label: 'New Quote Approval',   to: '/quote/approval/input',  keywords: ['approval'] },
  { id: 'qm-c-followup',   group: 'Quotes', icon: '🔁', label: 'New Quote Follow-Up',  to: '/quote/follow-up/input', keywords: ['followup'] },

  // ── Reporting ─────────────────────────────────────────────────────────────
  { id: 'rpt-v-reports',   group: 'Reporting', icon: '📋', label: 'All Reports',          to: '/reporting/report/list',           keywords: ['reports'] },
  { id: 'rpt-v-fields',    group: 'Reporting', icon: '🔢', label: 'Report Fields',        to: '/reporting/report-field/list',     keywords: ['fields', 'columns'] },
  { id: 'rpt-v-filters',   group: 'Reporting', icon: '🔽', label: 'Report Filters',       to: '/reporting/filter/list',           keywords: ['filters', 'conditions'] },
  { id: 'rpt-v-scheduled', group: 'Reporting', icon: '⏰', label: 'Scheduled Reports',    to: '/reporting/scheduled-report/list', keywords: ['scheduled', 'cron', 'automatic'] },
  { id: 'rpt-v-segments',  group: 'Reporting', icon: '🗂',  label: 'Segmentations',        to: '/reporting/segmentation/list',     keywords: ['segments', 'groups', 'cohorts'] },
  { id: 'rpt-v-analytics', group: 'Reporting', icon: '📉', label: 'Analytics Data',       to: '/reporting/analytics/list',        keywords: ['data', 'analytics'] },
  { id: 'rpt-v-trends',    group: 'Reporting', icon: '📈', label: 'Trend Analysis',       to: '/reporting/trend/list',            keywords: ['trends', 'analysis'] },
  { id: 'rpt-v-forecasts', group: 'Reporting', icon: '🔮', label: 'Forecasts',            to: '/reporting/forecast/list',         keywords: ['forecast', 'revenue', 'prediction'] },
  { id: 'rpt-c-report',    group: 'Reporting', icon: '📋', label: 'New Report',           to: '/reporting/report/input',          keywords: ['report'] },
  { id: 'rpt-c-field',     group: 'Reporting', icon: '🔢', label: 'New Report Field',     to: '/reporting/report-field/input',    keywords: ['field', 'column'] },
  { id: 'rpt-c-filter',    group: 'Reporting', icon: '🔽', label: 'New Report Filter',    to: '/reporting/filter/input',          keywords: ['filter'] },
  { id: 'rpt-c-scheduled', group: 'Reporting', icon: '⏰', label: 'New Scheduled Report', to: '/reporting/scheduled-report/input',keywords: ['scheduled', 'schedule'] },
  { id: 'rpt-c-segment',   group: 'Reporting', icon: '🗂',  label: 'New Segmentation',     to: '/reporting/segmentation/input',    keywords: ['segment', 'group'] },
  { id: 'rpt-c-analytics', group: 'Reporting', icon: '📉', label: 'New Analytics Entry',  to: '/reporting/analytics/input',       keywords: ['analytics', 'data'] },
  { id: 'rpt-c-trend',     group: 'Reporting', icon: '📈', label: 'New Trend Analysis',   to: '/reporting/trend/input',           keywords: ['trend', 'analysis'] },
  { id: 'rpt-c-forecast',  group: 'Reporting', icon: '🔮', label: 'New Forecast',         to: '/reporting/forecast/input',        keywords: ['forecast'] },

  // ── Security (superadmin only) ─────────────────────────────────────────────
  { id: 's-audit',     group: 'Security', icon: '🔒', label: 'Audit Logs',          to: '/security/audit-log/list',         keywords: ['audit', 'logs'],               superadminOnly: true },
  { id: 's-login',     group: 'Security', icon: '🔐', label: 'Login Activity',      to: '/security/login-activity/list',    keywords: ['login', 'sessions', 'auth'],   superadminOnly: true },
  { id: 's-access',    group: 'Security', icon: '🛡',  label: 'Access Control',      to: '/security/access-control/list',    keywords: ['access', 'permissions', 'rbac'],superadminOnly: true },
  { id: 's-integrity', group: 'Security', icon: '🔍', label: 'Data Integrity',      to: '/security/data-integrity/list',    keywords: ['integrity', 'validation'],     superadminOnly: true },
  { id: 's-alerts',    group: 'Security', icon: '⚠️', label: 'Security Alerts',     to: '/security/alert/list',             keywords: ['alerts', 'threats'],           superadminOnly: true },
  { id: 's-c-access',  group: 'Security', icon: '🛡',  label: 'New Access Control',  to: '/security/access-control/input',   keywords: ['access', 'permission', 'role'], superadminOnly: true },

  // ── Administrator (superadmin only) ────────────────────────────────────────
  { id: 'adm-tenants', group: 'Administrator', icon: '🏗',  label: 'All Tenants',  to: '/administrator/tenant/list', keywords: ['tenant', 'organisation', 'org'],  superadminOnly: true },
  { id: 'adm-users',   group: 'Administrator', icon: '👥', label: 'All Users',    to: '/administrator/user/list',   keywords: ['users', 'accounts', 'staff'],     superadminOnly: true },
];

const GROUP_COLORS = {
  Navigate:      '#3b82f6',
  Actions:       '#f97316',
  Create:        '#22c55e',
  Leads:         '#f59e0b',
  Opportunities: '#8b5cf6',
  Customers:     '#0ea5e9',
  Quotes:        '#e87860',
  Reporting:     '#10b981',
  Security:      '#ef4444',
  Administrator: '#e879f9',
};

const DEFAULT_GROUPS = ['Navigate', 'Actions', 'Create'];

const CommandPalette = ({ open, onClose, initialQuery = '' }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const superAdmin = checkSuperAdmin();
  const totalCommands = ALL_COMMANDS.filter(c => !c.superadminOnly || superAdmin).length;

  useEffect(() => {
    if (open) {
      setQuery(initialQuery || '');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [open, initialQuery]);

  const isSearching = query.trim().length > 0;

  const filtered = ALL_COMMANDS.filter(c => {
    if (c.superadminOnly && !superAdmin) return false;
    if (!isSearching) return DEFAULT_GROUPS.includes(c.group);
    const q = query.toLowerCase();
    return (
      c.label.toLowerCase().includes(q) ||
      c.group.toLowerCase().includes(q) ||
      (c.keywords || []).some(k => k.includes(q))
    );
  });

  const flatFiltered = filtered.map((cmd, i) => ({ ...cmd, flatIdx: i }));
  const grouped = flatFiltered.reduce((acc, cmd) => {
    if (!acc[cmd.group]) acc[cmd.group] = [];
    acc[cmd.group].push(cmd);
    return acc;
  }, {});

  const execute = useCallback((cmd) => {
    onClose();
    if (cmd.action) {
      cmd.action({ navigate });
    } else if (cmd.to) {
      navigate(cmd.to);
    }
  }, [navigate, onClose]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter')     { e.preventDefault(); if (filtered[activeIdx]) execute(filtered[activeIdx]); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, filtered, activeIdx, execute, onClose]);

  useEffect(() => { setActiveIdx(0); }, [query]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 9999,
        background: 'rgba(0,0,0,0.62)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '10vh',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 600, margin: '0 20px',
        background: 'var(--bg-card, #1e293b)',
        borderRadius: 16, overflow: 'hidden',
        boxShadow: '0 32px 80px rgba(0,0,0,0.55)',
        border: '1px solid var(--border, #334155)',
        animation: 'paletteIn 0.14s cubic-bezier(0.16,1,0.3,1)',
      }}>

        {/* Search bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '14px 18px',
          borderBottom: '1px solid var(--border, #334155)',
        }}>
          <span style={{ fontSize: 16, opacity: 0.55, flexShrink: 0 }}>🔍</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search commands, pages, actions…"
            style={{
              flex: 1, background: 'none', border: 'none', outline: 'none',
              fontSize: 15, color: 'var(--text-main, #f1f5f9)',
              caretColor: 'var(--color-primary, #3b82f6)',
            }}
          />
          {isSearching ? (
            <button
              onClick={() => setQuery('')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted, #94a3b8)', fontSize: 18, lineHeight: 1,
                padding: '0 4px', flexShrink: 0,
              }}
              aria-label="Clear search"
            >×</button>
          ) : (
            <kbd style={{
              fontSize: 11, padding: '3px 7px', borderRadius: 5, flexShrink: 0,
              background: 'var(--bg-hover)', color: 'var(--text-muted, #94a3b8)',
              border: '1px solid var(--border)', fontFamily: 'inherit',
            }}>ESC</kbd>
          )}
        </div>

        {/* Results */}
        <div ref={listRef} style={{ maxHeight: 440, overflowY: 'auto', paddingBottom: 6 }}>
          {filtered.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '40px 20px',
              color: 'var(--text-muted, #94a3b8)', fontSize: 13,
            }}>
              No commands match "<strong style={{ color: 'var(--text-sub)' }}>{query}</strong>"
            </div>
          ) : (
            Object.entries(grouped).map(([group, cmds]) => {
              const color = GROUP_COLORS[group] || '#3b82f6';
              return (
                <div key={group}>
                  <div style={{
                    padding: '10px 18px 4px',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
                      textTransform: 'uppercase', color,
                    }}>
                      {group}
                    </span>
                    <span style={{
                      fontSize: 10, color: 'var(--text-muted, #64748b)',
                    }}>
                      {cmds.length}
                    </span>
                  </div>
                  {cmds.map(cmd => {
                    const isActive = cmd.flatIdx === activeIdx;
                    return (
                      <div
                        key={cmd.id}
                        data-active={isActive}
                        onClick={() => execute(cmd)}
                        onMouseEnter={() => setActiveIdx(cmd.flatIdx)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '9px 18px', cursor: 'pointer',
                          background: isActive ? color + '14' : 'transparent',
                          borderLeft: `3px solid ${isActive ? color : 'transparent'}`,
                          transition: 'background 0.07s',
                        }}
                      >
                        <div style={{
                          width: 30, height: 30, borderRadius: 7, flexShrink: 0,
                          background: color + '1a',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14,
                        }}>
                          {cmd.icon}
                        </div>
                        <span style={{
                          flex: 1, fontSize: 13, fontWeight: 500,
                          color: 'var(--text-main, #f1f5f9)',
                        }}>
                          {cmd.label}
                        </span>
                        {isActive && (
                          <kbd style={{
                            fontSize: 10, padding: '2px 6px', borderRadius: 4,
                            background: 'var(--bg-hover)',
                            color: 'var(--text-muted, #94a3b8)',
                            border: '1px solid var(--border)',
                            fontFamily: 'inherit',
                          }}>↵</kbd>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}

          {/* Hint strip when showing defaults */}
          {!isSearching && (
            <div style={{
              margin: '6px 18px 4px',
              padding: '10px 14px',
              borderRadius: 8,
              background: 'var(--bg-stripe)',
              border: '1px dashed var(--border)',
              color: 'var(--text-muted, #64748b)',
              fontSize: 12,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ opacity: 0.7 }}>💡</span>
              Type to search all <strong style={{ color: 'var(--text-sub, #94a3b8)' }}>{totalCommands}</strong> commands across leads, opportunities, customers, quotes, and reporting.
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '9px 18px',
          borderTop: '1px solid var(--border, #334155)',
          display: 'flex', gap: 14, alignItems: 'center',
          fontSize: 11, color: 'var(--text-muted, #64748b)',
        }}>
          {[['↑↓', 'navigate'], ['↵', 'open'], ['ESC', 'close']].map(([k, l]) => (
            <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <kbd style={{ padding: '1px 5px', borderRadius: 3, border: '1px solid currentColor', fontSize: 10, fontFamily: 'inherit' }}>{k}</kbd>
              {l}
            </span>
          ))}
          <span style={{ marginLeft: 'auto' }}>
            {isSearching
              ? `${filtered.length} result${filtered.length !== 1 ? 's' : ''}`
              : `${totalCommands} commands`}
          </span>
        </div>
      </div>

      <style>{`
        @keyframes paletteIn {
          from { opacity: 0; transform: translateY(-10px) scale(0.97); }
          to   { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
};

export default CommandPalette;
